/**
 * pendingSharedFiles — in-memory handoff of OS-shared content (File[] and/or a
 * text/URL) to a target chat's composer.
 *
 * When the user shares photos/videos/files/links into the app (see useShareTarget
 * + ShareTargetModal) and picks a conversation, we cannot route the actual `File`
 * objects through the URL or React Router state (File is not serializable, and
 * the native navigation uses bare pushState). Nor can we use `useDrafts` — drafts
 * are persisted to localStorage as plain JSON.
 *
 * Instead ShareTargetModal stashes the payload here keyed by the target thread id,
 * navigates to that chat, and MessageInput consumes it once for the matching
 * `currentThreadId` (attaching files via addAttachment and seeding the composer
 * text). Consume-once semantics (delete on read) make this safe against React
 * StrictMode double-mounts and prevent a stale share from leaking into the wrong
 * conversation later.
 */

export interface PendingShare {
  files: File[];
  text?: string;
}

const byThread = new Map<string, PendingShare>();

/** Stash shared content for `threadId`'s composer to pick up. */
export function setPendingShared(threadId: string, share: PendingShare): void {
  if (!threadId) return;
  if (!share.files?.length && !share.text) return;
  byThread.set(String(threadId), { files: share.files || [], text: share.text });
}

/** Return and clear any content stashed for `threadId` (consume-once). */
export function takePendingShared(threadId: string): PendingShare | undefined {
  const key = String(threadId);
  const share = byThread.get(key);
  if (share) byThread.delete(key);
  return share;
}
